/**
 * Custom hook for PowerEditor action handlers
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ViewportSizeType } from "@/types/viewport-device.types";
import { ViewMode } from "@/types/power-editor.types";
import { resolveAppServiceFromPort, extractErrorMessage } from "@/lib/utils/power-editor-utils";
import { devContainerAPI } from "@/lib/dev-containers/api";
import { projectsApi } from "@/lib/api/projects";
import { editorAPI } from "@/lib/editor/api";
import { useAuth } from "@/contexts/AuthContext";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { usePagesStore } from "@/stores/pages-store";
import { visualEditorService } from "@/lib/visual-editor/visual-editor-service";

interface UsePowerEditorActionsProps {
  projectId: string;
  project: any;
  workspace: any;
  previewIframeRef: React.RefObject<HTMLIFrameElement> | null;
  visualEditorRef: React.MutableRefObject<any>;
  pollingCleanupRef: React.MutableRefObject<(() => void) | null>;
  previewIframeForPollingRef: React.MutableRefObject<HTMLIFrameElement | null>;
  computedActualPreviewUrl: string;
  showPreview: boolean;
  isDevelopment: boolean;

  // Setters from state hook
  setProject: (project: any) => void;
  setCurrentProject: (project: any) => void;
  setWorkspace: (workspace: any) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAccessUrl: (url: string | null) => void;
  setPreviewUrl: (url: string | null) => void;
  setIsResuming: (resuming: boolean) => void;
  setIsPreviewLoading: (loading: boolean) => void;
  setIsShowingStaticPreview: (showing: boolean) => void;
  setAppService: (service: any) => void;
  setRetryCount: React.Dispatch<React.SetStateAction<number>>;
  setViewMode: (mode: ViewMode) => void;
  setIsSidebarHidden: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPreview: (show: boolean) => void;
  setIsPreviewDisabled: (disabled: boolean) => void;
  setIsSavingChanges: (saving: boolean) => void;
  setCommitState: (state: "idle" | "generating" | "creating") => void;
  setIsResponsiveMode: (responsive: boolean) => void;
  setViewportSize: (size: ViewportSizeType) => void;
  setIsCustomViewport: (custom: boolean) => void;
  setCustomWidth: (width: number) => void;
  setCustomHeight: (height: number) => void;
  setCurrentPath: React.Dispatch<React.SetStateAction<string>>;
  setShowDeviceDropdown: (show: boolean) => void;
  setShowPathDropdown: React.Dispatch<React.SetStateAction<boolean>>;
}

export function usePowerEditorActions({
  projectId,
  project,
  workspace,
  previewIframeRef,
  visualEditorRef,
  pollingCleanupRef,
  previewIframeForPollingRef,
  computedActualPreviewUrl,
  showPreview,
  isDevelopment,

  // Setters
  setProject,
  setCurrentProject,
  setWorkspace,
  setLoading,
  setError,
  setAccessUrl,
  setPreviewUrl,
  setIsResuming,
  setIsPreviewLoading,
  setIsShowingStaticPreview,
  setAppService,
  setRetryCount,
  setViewMode,
  setIsSidebarHidden,
  setShowPreview,
  setIsPreviewDisabled,
  setIsSavingChanges,
  setCommitState,
  setIsResponsiveMode,
  setViewportSize,
  setIsCustomViewport,
  setCustomWidth,
  setCustomHeight,
  setCurrentPath,
  setShowDeviceDropdown,
  setShowPathDropdown,
}: UsePowerEditorActionsProps) {
  const navigate = useNavigate();
  const { getIdToken } = useAuth();
  const setAvailablePaths = usePagesStore((s) => s.setPages);

  // Poll preview URL to check if service is ready
  const pollPreviewReadiness = useCallback((previewUrlToCheck: string) => {
    let attempts = 0;
    const maxAttempts = 20;

    const intervalId = setInterval(() => {
      attempts++;

      if (previewIframeForPollingRef.current) {
        const currentSrc = previewIframeForPollingRef.current.src;
        previewIframeForPollingRef.current.src = "";
        setTimeout(() => {
          if (previewIframeForPollingRef.current) {
            previewIframeForPollingRef.current.src = currentSrc;
          }
        }, 100);
      }

      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
        setIsPreviewLoading(false);
      }
    }, 2000);

    const maxTimeoutId = setTimeout(() => {
      clearInterval(intervalId);
      setIsPreviewLoading(false);
    }, 45000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(maxTimeoutId);
    };
  }, [previewIframeForPollingRef, setIsPreviewLoading]);

  const loadProjectAndWorkspace = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getIdToken();
      if (!token) return;

      const projectData = await projectsApi.getOne(token, projectId);
      setProject(projectData);
      setCurrentProject(projectData);

      if (projectData.mode !== "power") {
        navigate(`/dashboard/editor/${projectId}`);
        return;
      }

      if (!projectData.devContainerId) {
        setError("This project is not associated with a dev container");
        return;
      }

      const workspaceData = await devContainerAPI.getDevContainer(
        projectData.devContainerId
      );
      setWorkspace(workspaceData);

      if (
        workspaceData.status === "running" ||
        workspaceData.status === "paused"
      ) {
        // Resolve app service for preview URL construction
        let resolvedAppService = resolveAppServiceFromPort(undefined);

        if (workspaceData.templateId) {
          try {
            const templateData = await editorAPI.getTemplate(
              workspaceData.templateId
            );
            const templatePort =
              templateData.appPort || templateData.config?.appPort;

            resolvedAppService = resolveAppServiceFromPort(templatePort);
          } catch (templateErr) {
            console.warn(
              "[PowerEditor] Failed to resolve template, using default service:",
              templateErr
            );
          }
        }

        setAppService(resolvedAppService);

        // Handle PAUSED containers - show UI immediately, trigger resume in background
        if (workspaceData.status === "paused") {
          setIsResuming(true);

          // Show static preview if available, otherwise show loading
          if (projectData.latestPreviewUrl) {
            console.log('[PowerEditor] Fast resume: showing static preview URL', projectData.latestPreviewUrl);
            setPreviewUrl(projectData.latestPreviewUrl);
            setIsShowingStaticPreview(true);
            setIsPreviewLoading(false);
          } else {
            console.log('[PowerEditor] Resume: no static preview, showing loading state');
            setIsPreviewLoading(true);
          }

          // TRIGGER THE RESUME - calls backend which auto-resumes paused containers
          console.log('[PowerEditor] Triggering container resume...');
          try {
            const urlData = await devContainerAPI.getDevContainerAccessUrl(
              workspaceData.id
            );
            if (urlData?.url) {
              setAccessUrl(urlData.url);
            }
          } catch (err) {
            console.error("[PowerEditor] Failed to trigger resume:", err);
          }

          // Poll for container to become running, then switch to live preview
          const pollForRunning = async () => {
            const maxAttempts = 60; // 2 minutes max
            let attempts = 0;

            while (attempts < maxAttempts) {
              attempts++;
              await new Promise(resolve => setTimeout(resolve, 2000));

              try {
                const updatedWorkspace = await devContainerAPI.getDevContainer(
                  workspaceData.id
                );

                console.log(`[PowerEditor] Poll ${attempts}: status = ${updatedWorkspace.status}`);

                if (updatedWorkspace.status === "running") {
                  console.log('[PowerEditor] Container is running, fetching live URLs');
                  setWorkspace(updatedWorkspace);

                  // Check if post-install failed (e.g. pnpm install) — code is there but deps aren't
                  if (updatedWorkspace.errorMessage) {
                    console.warn('[PowerEditor] Post-install failed:', updatedWorkspace.errorMessage);
                    toast.error(updatedWorkspace.errorMessage);
                    setShowPreview(false);
                    setIsPreviewDisabled(true);
                    setIsResuming(false);
                    setIsPreviewLoading(false);
                    return;
                  }

                  // Fetch live preview URL
                  try {
                    const serviceUrlData = await devContainerAPI.getServiceAccessUrl(
                      updatedWorkspace.id,
                      resolvedAppService.service
                    );
                    if (serviceUrlData.url) {
                      console.log('[PowerEditor] Live preview URL ready:', serviceUrlData.url);
                      setPreviewUrl(serviceUrlData.url);
                      setIsShowingStaticPreview(false);

                      // Start polling to wait for vite dev server to actually respond
                      // The container is "running" but vite needs ~10s more to start
                      if (pollingCleanupRef.current) {
                        pollingCleanupRef.current();
                      }
                      setIsPreviewLoading(true);
                      pollingCleanupRef.current = pollPreviewReadiness(serviceUrlData.url);
                    }
                  } catch (err) {
                    console.error("[PowerEditor] Failed to get service URL:", err);
                  }

                  // Refresh access URL now that container is running
                  try {
                    const urlData = await devContainerAPI.getDevContainerAccessUrl(
                      updatedWorkspace.id
                    );
                    if (urlData?.url) {
                      setAccessUrl(urlData.url);
                    }
                  } catch (err) {
                    console.error("[PowerEditor] Failed to get IDE access URL:", err);
                  }

                  setIsResuming(false);
                  return;
                }

                setWorkspace(updatedWorkspace);
              } catch (err) {
                console.error("[PowerEditor] Poll failed:", err);
              }
            }

            // Timeout
            console.error('[PowerEditor] Resume timeout - container did not become running');
            setIsResuming(false);
            setIsPreviewLoading(false);
            setError("Workspace resume timed out. Please try refreshing the page.");
          };

          // Start polling in background (don't await)
          pollForRunning();

        } else {
          // Container is RUNNING - fetch URLs and poll for readiness
          // (warm pool containers may need time for services to initialize)

          // Check if container has an error (e.g. post-install failed on previous resume)
          if (workspaceData.errorMessage) {
            console.warn('[PowerEditor] Container has error:', workspaceData.errorMessage);
            toast.error(workspaceData.errorMessage);
            setShowPreview(false);
            setIsPreviewDisabled(true);
          }

          try {
            const urlData = await devContainerAPI.getDevContainerAccessUrl(
              workspaceData.id
            );
            if (urlData?.url) {
              setAccessUrl(urlData.url);
            }
          } catch (err) {
            console.error("[PowerEditor] Failed to get IDE access URL:", err);
          }

          const serviceUrlData = await devContainerAPI.getServiceAccessUrl(
            workspaceData.id,
            resolvedAppService.service
          );
          if (serviceUrlData.url) {
            console.log('[PowerEditor] Live preview URL ready:', serviceUrlData.url);
            setPreviewUrl(serviceUrlData.url);
            setIsShowingStaticPreview(false);

            if (pollingCleanupRef.current) {
              pollingCleanupRef.current();
              pollingCleanupRef.current = null;
            }

            // Start polling to wait for preview service to be ready
            // This is important for warm pool containers that just got handed over
            setIsPreviewLoading(true);
            pollingCleanupRef.current = pollPreviewReadiness(serviceUrlData.url);
          }
        }
      } else if (
        workspaceData.status === "provisioning" ||
        workspaceData.status === "starting"
      ) {
        setTimeout(() => {
          setRetryCount((prev: number) => prev + 1);
          loadProjectAndWorkspace();
        }, 5000);
      }
    } catch (err: any) {
      console.error("Failed to load project/workspace:", err);
      setError(extractErrorMessage(err, "Failed to load workspace"));
    } finally {
      setLoading(false);
    }
  }, [
    projectId,
    getIdToken,
    navigate,
    setProject,
    setCurrentProject,
    setWorkspace,
    setLoading,
    setError,
    setIsResuming,
    setIsShowingStaticPreview,
    setAppService,
    setAccessUrl,
    setPreviewUrl,
    setIsPreviewLoading,
    setRetryCount,
    pollingCleanupRef,
    pollPreviewReadiness,
  ]);

  const handleStartWorkspace = useCallback(async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      await devContainerAPI.startDevContainer(workspace.id);
      loadProjectAndWorkspace();
    } catch (err: any) {
      setError(extractErrorMessage(err, "Failed to start workspace"));
      setLoading(false);
    }
  }, [workspace, setLoading, setError, loadProjectAndWorkspace]);

  // Handle container paused during browser sleep/inactivity
  const handleContainerPausedDuringSleep = useCallback(async () => {
    if (!workspace?.id) return;

    console.debug('[PowerEditor] Container paused during sleep, resuming...');
    setIsResuming(true);

    try {
      // Call access-url which auto-resumes paused containers (existing backend logic)
      const urlData = await devContainerAPI.getDevContainerAccessUrl(workspace.id);
      if (urlData?.url) {
        setAccessUrl(urlData.url);
      }

      // Refresh workspace data to get updated status
      const updatedWorkspace = await devContainerAPI.getDevContainer(workspace.id);
      setWorkspace(updatedWorkspace);

      // Force page reload to reinitialize IDE with fresh connection
      // This is necessary because:
      // 1. In dev mode, the proxy URL doesn't change even after resume
      // 2. VS Code server may have restarted, breaking WebSocket connections
      // 3. Session state inside the iframe is stale
      window.location.reload();
    } catch (err: any) {
      console.error('[PowerEditor] Failed to resume container:', err);
      setError(extractErrorMessage(err, 'Failed to resume workspace'));
    } finally {
      setIsResuming(false);
    }
  }, [workspace?.id, setIsResuming, setAccessUrl, setWorkspace, setError]);

  // Fetch routes via pages API (returns PageInfo[] with registered status)
  const fetchRoutes = useCallback(async () => {
    if (
      !workspace?.id ||
      (workspace.status !== "running" && workspace.status !== "paused")
    ) {
      return;
    }

    try {
      const response = await visualEditorService.getPages(workspace.id);

      if (response.success && response.pages.length > 0) {
        setAvailablePaths(response.pages);

        setCurrentPath((prevPath: string) => {
          const routes = response.pages.map((p) => p.route);
          if (!routes.includes(prevPath)) {
            return routes[0];
          }
          return prevPath;
        });
      }
    } catch (error) {
      console.error("[PowerEditor] Failed to fetch routes:", error);
    }
  }, [workspace?.id, workspace?.status, setAvailablePaths, setCurrentPath]);

  // Preview actions
  const handlePreviewLoaded = useCallback(() => {
    if (pollingCleanupRef.current) {
      pollingCleanupRef.current();
      pollingCleanupRef.current = null;
    }
    setIsPreviewLoading(false);
  }, [pollingCleanupRef, setIsPreviewLoading]);

  const handleOpenPreviewInNewWindow = useCallback(() => {
    window.open(computedActualPreviewUrl, "_blank");
  }, [computedActualPreviewUrl]);

  const handleReloadPreview = useCallback(() => {
    if (previewIframeRef?.current && computedActualPreviewUrl) {
      // Add cache-busting timestamp to force fresh load
      const url = new URL(computedActualPreviewUrl);
      url.searchParams.set('_cb', Date.now().toString());
      previewIframeRef.current.src = url.toString();
    }
  }, [previewIframeRef, computedActualPreviewUrl]);

  const handleExpandPreview = useCallback(() => {
    setIsSidebarHidden((prev: boolean) => !prev);
  }, [setIsSidebarHidden]);

  // Visual editor actions
  const handleSaveChanges = useCallback(async () => {
    if (visualEditorRef.current?.handleSave) {
      setIsSavingChanges(true);
      setCommitState("idle");
      try {
        await visualEditorRef.current.handleSave();
      } finally {
        setIsSavingChanges(false);
        setCommitState("idle");
      }
    }
  }, [visualEditorRef, setIsSavingChanges, setCommitState]);

  const handleDiscardChanges = useCallback(() => {
    if (visualEditorRef.current?.handleDiscard) {
      visualEditorRef.current.handleDiscard();

      setTimeout(() => {
        handleReloadPreview();
      }, 100);
    }
  }, [visualEditorRef, handleReloadPreview]);

  const handleUndo = useCallback(() => {
    if (visualEditorRef.current?.handleUndo) {
      visualEditorRef.current.handleUndo();
    }
  }, [visualEditorRef]);

  const handleRedo = useCallback(() => {
    if (visualEditorRef.current?.handleRedo) {
      visualEditorRef.current.handleRedo();
    }
  }, [visualEditorRef]);

  // Viewport actions
  const handleViewportResize = useCallback(
    (width: number, height: number) => {
      setCustomWidth(width);
      setCustomHeight(height);
    },
    [setCustomWidth, setCustomHeight]
  );

  // Path actions
  const handlePathChange = useCallback((path: string) => {
    setCurrentPath(path);
  }, [setCurrentPath]);

  const handlePathSelect = useCallback(
    (path: string) => {
      if (previewIframeRef?.current) {
        previewMessage.navigateToRoute(previewIframeRef.current, path);
      }
      setCurrentPath(path);
      setShowPathDropdown(false);
    },
    [previewIframeRef, setCurrentPath, setShowPathDropdown]
  );

  const handleTogglePathDropdown = useCallback(() => {
    setShowPathDropdown((prev: boolean) => !prev);
    if (!showPreview) setShowPreview(true);
  }, [setShowPathDropdown, showPreview, setShowPreview]);

  return {
    loadProjectAndWorkspace,
    handleStartWorkspace,
    handleContainerPausedDuringSleep,
    fetchRoutes,
    handlePreviewLoaded,
    handleOpenPreviewInNewWindow,
    handleReloadPreview,
    handleExpandPreview,
    handleSaveChanges,
    handleDiscardChanges,
    handleUndo,
    handleRedo,
    handleViewportResize,
    handlePathChange,
    handlePathSelect,
    handleTogglePathDropdown,
  };
}